import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import AdminRoute from './components/common/AdminRoute';
import ScrollToTop from './components/common/ScrollToTop';
import Layout from './components/layout/Layout';
import ClassificationPage from './pages/ClassificationPage';
import ClassificationDetailsPage from './pages/ClassificationDetailsPage';
import SharedClassificationPage from './pages/SharedClassificationPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import ProfilePage from './pages/ProfilePage';
import RegisterPage from './pages/RegisterPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import HowItWorksPage from './pages/HowItWorksPage';
import FeaturesPage from './pages/FeaturesPage';
import AboutPage from './pages/AboutPage';
import LeaderboardPage from './pages/LeaderboardPage';
import RewardsPage from './pages/RewardsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import ContactUsPage from './pages/ContactUsPage';
import HistoryPage from './pages/HistoryPage';
import FAQPage from './pages/FAQPage';
import NotFound from './pages/NotFound';
import TipsPage from './pages/TipsPage';

// Category Pages
import RecyclableWastePage from './pages/Categories/RecyclableWastePage';
import OrganicWastePage from './pages/Categories/OrganicWastePage';
import HazardousWastePage from './pages/Categories/HazardousWastePage';
import GeneralWastePage from './pages/Categories/GeneralWastePage';

// Admin Pages
import AdminDashboard from './pages/Admin/AdminDashboard';
import UserManagement from './pages/Admin/UserManagement';
import CategoryManagement from './pages/Admin/CategoryManagement';
import Stats from './pages/Admin/Stats';
import Logs from './pages/Admin/Logs';
import AdminTips from './pages/Admin/AdminTips';
import AdminFeedback from './pages/Admin/AdminFeedback';
import AdminBadgeManagement from './components/admin/AdminBadgeManagement';

function App() {
    return (
        <AuthProvider>
            <Router>
                <ScrollToTop />
                <Routes>
                    {/* Routes that will have the Header and Footer */}
                    <Route element={<Layout />}>
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/classify" element={<ClassificationPage />} />
                        <Route path="/tips" element={<TipsPage />} />
                        <Route path="/how-it-works" element={<HowItWorksPage />} />
                        <Route path="/features" element={<FeaturesPage />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/leaderboard" element={<LeaderboardPage />} />
                        <Route path="/rewards" element={<RewardsPage />} />
                        <Route path="/privacy" element={<PrivacyPolicyPage />} />
                        <Route path="/terms" element={<TermsOfServicePage />} />
                        <Route path="/contact" element={<ContactUsPage />} />
                        <Route path="/faq" element={<FAQPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/verify-email" element={<VerifyEmailPage />} />
                        <Route path="/register" element={<RegisterPage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />

                        {/* Category Pages - Public routes */}
                        <Route path="/categories/recycle" element={<RecyclableWastePage />} />
                        <Route path="/categories/compost" element={<OrganicWastePage />} />
                        <Route path="/categories/hazardous" element={<HazardousWastePage />} />
                        <Route path="/categories/general" element={<GeneralWastePage />} />

                        {/* Public sharing routes - no authentication required */}
                        <Route path="/shared-classification/:shareId" element={<SharedClassificationPage />} />
                        <Route path="/shared/:shareId" element={<SharedClassificationPage />} />

                        {/* Protected Routes - redirect to home page if not authenticated */}
                        <Route
                            path="/profile"
                            element={
                                <ProtectedRoute redirectTo="/">
                                    <ProfilePage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/history"
                            element={
                                <ProtectedRoute redirectTo="/">
                                    <HistoryPage />
                                </ProtectedRoute>
                            }
                        />

                        {/* Classification details route - protected */}
                        <Route
                            path="/classification/:id"
                            element={
                                <ProtectedRoute redirectTo="/">
                                    <ClassificationDetailsPage />
                                </ProtectedRoute>
                            }
                        />

                        {/* Admin Routes - Only accessible by ADMIN or SUPER_ADMIN */}
                        <Route
                            path="/admin"
                            element={
                                <AdminRoute redirectTo="/">
                                    <AdminDashboard />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/dashboard"
                            element={
                                <AdminRoute redirectTo="/">
                                    <AdminDashboard />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/users"
                            element={
                                <AdminRoute redirectTo="/">
                                    <UserManagement />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/categories"
                            element={
                                <AdminRoute redirectTo="/">
                                    <CategoryManagement />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/tips"
                            element={
                                <AdminRoute redirectTo="/">
                                    <AdminTips />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/stats"
                            element={
                                <AdminRoute redirectTo="/">
                                    <Stats />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/logs"
                            element={
                                <AdminRoute redirectTo="/">
                                    <Logs />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/badges"
                            element={
                                <AdminRoute redirectTo="/">
                                    <AdminBadgeManagement />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/feedback"
                            element={
                                <AdminRoute redirectTo="/">
                                    <AdminFeedback />
                                </AdminRoute>
                            }
                        />

                        {/* 404 Not Found - must be last */}
                        <Route path="*" element={<NotFound />} />
                    </Route>

                    {/* Standalone routes that will NOT have the Header and Footer */}
                    <Route path="/login" element={<LoginPage />} />
                </Routes>
            </Router>
        </AuthProvider>
    );
}

export default App;